import React, { useEffect, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Activity, Users, Calendar, Euro, ClipboardCheck, UserPlus, Clock, ArrowUpRight } from "lucide-react";
import { format, startOfWeek, endOfWeek, formatDistanceToNow, subMonths, getMonth, startOfMonth, endOfMonth } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { DashboardSkeleton } from "@/components/shared/DashboardSkeleton";

type DashboardData = {
  activeStudents: number;
  monthlyRevenue: number;
  lastMonthRevenue: number;
  scheduledSessions: number;
  pendingRequests: number;
  pendingAssessments: number;
  newStudentRequests: number;
  lastWeekSessions: number;
  pendingClassRequests: any[];
  newStudentRequestsDetails: any[];
  revenue: {
    month: string;
    valor: number;
  }[];
  sessions: {
    month: string;
    sessoes: number;
  }[];
  upcomingEvents: any[];
};

export const DashboardOverview = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const { getPath } = useLanguagePath();
  const dateLocale = language === 'pt' ? ptBR : enUS;

  const {
    data: dashboardData,
    isLoading
  } = useQuery({
    queryKey: ["dashboard-data", user?.id],
    queryFn: async () => {
      const { data: studentsData, error: studentsError } = await supabase
        .from("students")
        .select("monthly_fee")
        .eq("trainer_id", user?.id)
        .eq("status", "active");
      
      if (studentsError) throw studentsError;

      const monthlyRevenue = studentsData.reduce((total, student) => {
        return total + (student.monthly_fee || 0);
      }, 0);

      const lastMonthDate = subMonths(new Date(), 1);
      const lastMonthStart = startOfMonth(lastMonthDate);
      const lastMonthEnd = endOfMonth(lastMonthDate);
      
      const { data: lastMonthStudents, error: lastMonthError } = await supabase
        .from("students")
        .select("monthly_fee")
        .eq("trainer_id", user?.id)
        .eq("status", "active")
        .lt("created_at", lastMonthEnd.toISOString());

      if (lastMonthError) throw lastMonthError;

      const lastMonthRevenue = lastMonthStudents.reduce((total, student) => {
        return total + (student.monthly_fee || 0);
      }, 0);

      const revenueData = [];
      for (let i = 6; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthStart = startOfMonth(monthDate);
        const monthEnd = endOfMonth(monthDate);
        
        const { data: monthStudents } = await supabase
          .from("students")
          .select("monthly_fee")
          .eq("trainer_id", user?.id)
          .eq("status", "active")
          .lt("created_at", monthEnd.toISOString());

        const monthRevenue = monthStudents?.reduce((total, student) => {
          return total + (student.monthly_fee || 0);
        }, 0) || 0;

        revenueData.push({
          month: format(monthDate, 'MMM', { locale: dateLocale }),
          valor: monthRevenue
        });
      }

      const { data: scheduleRequests, error: requestsError } = await supabase
        .from("schedule_requests")
        .select(`
          *,
          profiles:student_id (
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq("trainer_id", user?.id)
        .eq("status", "pending")
        .order('requested_date', { ascending: true });
      if (requestsError) throw requestsError;

      const { data: upcomingEvents, error: upcomingEventsError } = await supabase
        .from("schedule_events")
        .select(`
          *,
          student:student_id (
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq("trainer_id", user?.id)
        .eq("status", "scheduled")
        .gte("start_time", new Date().toISOString())
        .order('start_time', { ascending: true })
        .limit(5);
      if (upcomingEventsError) throw upcomingEventsError;

      const { data: studentRequests, error: studentRequestsError } = await supabase
        .from("student_requests")
        .select("*")
        .eq("trainer_id", user?.id)
        .eq("status", "pending")
        .order('created_at', { ascending: true });
      if (studentRequestsError) throw studentRequestsError;

      const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
      const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });

      const { data: scheduledSessions, error: sessionsError } = await supabase
        .from("schedule_events")
        .select("*")
        .eq("trainer_id", user?.id)
        .eq("status", "scheduled")
        .gte("start_time", weekStart.toISOString())
        .lte("start_time", weekEnd.toISOString());
      if (sessionsError) throw sessionsError;

      const sixMonthsAgo = subMonths(new Date(), 6);
      const { data: sessionsData, error: sessionsHistoryError } = await supabase
        .from("schedule_events")
        .select("start_time")
        .eq("trainer_id", user?.id)
        .in("status", ["completed", "scheduled"])
        .gte("start_time", sixMonthsAgo.toISOString());

      if (sessionsHistoryError) throw sessionsHistoryError;

      const sessionsByMonth = new Array(7).fill(null).map((_, index) => {
        const currentMonth = subMonths(new Date(), index);
        const monthStart = startOfMonth(currentMonth);
        const monthEnd = endOfMonth(currentMonth);

        const sessionsInMonth = sessionsData?.filter(session => {
          const sessionDate = new Date(session.start_time);
          return sessionDate >= monthStart && sessionDate <= monthEnd;
        }) || [];

        return {
          month: format(currentMonth, 'MMM', { locale: dateLocale }),
          sessoes: sessionsInMonth.length
        };
      }).reverse();

      return {
        activeStudents: studentsData?.length || 0,
        monthlyRevenue,
        lastMonthRevenue,
        scheduledSessions: scheduledSessions?.length || 0,
        pendingRequests: scheduleRequests?.length || 0,
        pendingAssessments: 0,
        newStudentRequests: studentRequests?.length || 0,
        lastWeekSessions: scheduledSessions?.length || 0,
        pendingClassRequests: scheduleRequests || [],
        newStudentRequestsDetails: studentRequests || [],
        revenue: revenueData,
        sessions: sessionsByMonth,
        upcomingEvents: upcomingEvents || []
      };
    },
    enabled: !!user?.id,
    refetchInterval: 30000
  });

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (!dashboardData) {
    return <div>Erro ao carregar dados</div>;
  }

  const calculateRevenueChange = () => {
    if (dashboardData.monthlyRevenue === 0 && dashboardData.lastMonthRevenue === 0) {
      return 0;
    }
    
    if (dashboardData.lastMonthRevenue === 0) {
      return dashboardData.monthlyRevenue > 0 ? 100 : 0;
    }
    
    return (dashboardData.monthlyRevenue - dashboardData.lastMonthRevenue) / dashboardData.lastMonthRevenue * 100;
  };
  
  const revenueChange = calculateRevenueChange();

  return <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight gradient-text text-slate-950 dark:text-slate-50">{t('dashboard.overview.title')}</h2>
          <p className="text-muted-foreground">
            {t('dashboard.overview.subtitle')}
          </p>
          <Breadcrumb className="flex-wrap">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">{t('dashboard.overview.dashboard')}</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium">{t('dashboard.overview.title')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
      
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Link to={getPath("/dashboard/students")} className="transition-transform hover:scale-[1.02]">
          <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden h-full hover:shadow-lg transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-gray-300">{t('dashboard.overview.cards.activeStudents')}</CardTitle>
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Users className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800 dark:text-gray-100">{dashboardData.activeStudents}</div>
              <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center mt-1">
                {t('dashboard.overview.cards.activeSubscriptions')}
                <ArrowUpRight className="h-3 w-3 ml-1 text-flame" />
              </p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to={getPath("/dashboard/schedule")} className="transition-transform hover:scale-[1.02]">
          <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden h-full hover:shadow-lg transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-gray-300">
                {t('dashboard.overview.cards.classRequests')}
              </CardTitle>
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Calendar className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800 dark:text-gray-100">{dashboardData.pendingRequests}</div>
              <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center mt-1">
                {t('dashboard.overview.cards.awaitingApproval')}
                <ArrowUpRight className="h-3 w-3 ml-1 text-flame" />
              </p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to={getPath("/dashboard/students")} className="transition-transform hover:scale-[1.02]">
          <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden h-full hover:shadow-lg transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-gray-300">
                {t('dashboard.overview.cards.newStudentRequests')}
              </CardTitle>
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <UserPlus className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800 dark:text-gray-100">{dashboardData.newStudentRequests}</div>
              <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center mt-1">
                {t('dashboard.overview.cards.awaitingResponse')}
                <ArrowUpRight className="h-3 w-3 ml-1 text-flame" />
              </p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to={getPath("/dashboard/schedule")} className="transition-transform hover:scale-[1.02]">
          <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden h-full hover:shadow-lg transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-gray-300">
                {t('dashboard.overview.cards.weeklyClasses')}
              </CardTitle>
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Clock className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800 dark:text-gray-100">{dashboardData.scheduledSessions}</div>
              <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center mt-1">
                {format(startOfWeek(new Date(), {
                weekStartsOn: 1
              }), "dd/MM", {
                locale: dateLocale
              })} - {format(endOfWeek(new Date(), {
                weekStartsOn: 1
              }), "dd/MM", {
                locale: dateLocale
              })}
                <ArrowUpRight className="h-3 w-3 ml-1 text-flame" />
              </p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to={getPath("/dashboard/assessments")} className="transition-transform hover:scale-[1.02]">
          <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden h-full hover:shadow-lg transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-gray-300">
                {t('dashboard.overview.cards.pendingAssessments')}
              </CardTitle>
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <ClipboardCheck className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800 dark:text-gray-100">{dashboardData.pendingAssessments}</div>
              <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center mt-1">
                {t('dashboard.overview.cards.forThisWeek')}
                <ArrowUpRight className="h-3 w-3 ml-1 text-flame" />
              </p>
            </CardContent>
          </Card>
        </Link>
        
        <Link to={getPath("/dashboard/settings")} className="transition-transform hover:scale-[1.02]">
          <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden h-full hover:shadow-lg transition-all">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 relative">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
              <CardTitle className="text-sm font-medium text-slate-600 dark:text-gray-300">
                {t('dashboard.overview.cards.monthlyRevenue')}
              </CardTitle>
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Euro className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-800 dark:text-gray-100">{dashboardData.monthlyRevenue.toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', {
                style: 'currency',
                currency: 'EUR'
              })}</div>
              <p className="text-xs text-muted-foreground dark:text-gray-400 flex items-center mt-1">
                {revenueChange >= 0 ? '+' : ''}{revenueChange.toFixed(1)}% {t('dashboard.overview.cards.comparedToPrevious')}
                <ArrowUpRight className="h-3 w-3 ml-1 text-flame" />
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="col-span-1 border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden">
          <CardHeader className="pb-2 relative">
            <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
            <CardTitle className="text-lg font-medium flex items-center gap-2 text-slate-700 dark:text-gray-300">
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Activity className="h-5 w-5" />
              </div>
              {t('dashboard.overview.charts.monthlyRevenue')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dashboardData.revenue} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#DA5E29" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#DA5E29" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{
                fill: '#63768D'
              }} axisLine={{
                stroke: '#E4E9F5'
              }} />
                <YAxis stroke="#9CA3AF" tick={{
                fill: '#63768D'
              }} axisLine={{
                stroke: '#E4E9F5'
              }} tickFormatter={value => value.toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', {
                style: 'currency',
                currency: 'EUR',
                maximumFractionDigits: 0
              })} />
                <Tooltip formatter={value => [value.toLocaleString(language === 'pt' ? 'pt-PT' : 'en-US', {
                style: 'currency',
                currency: 'EUR'
              }), t('dashboard.overview.charts.revenue')]} labelFormatter={label => `${language === 'pt' ? 'Mês' : 'Month'}: ${label}`} contentStyle={{
                backgroundColor: 'rgba(255, 255, 252, 0.9)',
                borderRadius: '8px',
                border: '1px solid #E4E9F5',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }} />
                <Area type="monotone" dataKey="valor" stroke="#DA5E29" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-1 border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden">
          <CardHeader className="pb-2 relative">
            <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
            <CardTitle className="text-lg font-medium flex items-center gap-2 text-slate-700 dark:text-gray-300">
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Calendar className="h-5 w-5" />
              </div>
              {t('dashboard.overview.charts.sessionsPerMonth')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={dashboardData.sessions} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis dataKey="month" stroke="#9CA3AF" tick={{
                fill: '#63768D'
              }} axisLine={{
                stroke: '#E4E9F5'
              }} />
                <YAxis stroke="#9CA3AF" tick={{
                fill: '#63768D'
              }} axisLine={{
                stroke: '#E4E9F5'
              }} />
                <Tooltip formatter={value => [`${value}`, t('dashboard.overview.charts.sessions')]} labelFormatter={label => `${language === 'pt' ? 'Mês' : 'Month'}: ${label}`} contentStyle={{
                backgroundColor: 'rgba(255, 255, 252, 0.9)',
                borderRadius: '8px',
                border: '1px solid #E4E9F5',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }} />
                <Legend verticalAlign="top" height={36} />
                <Bar dataKey="sessoes" name={t('dashboard.overview.charts.sessions')} fill="#DA5E29" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden">
          <CardHeader className="pb-3 relative">
            <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
            <CardTitle className="text-lg font-medium flex items-center gap-2 text-slate-700 dark:text-gray-300">
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Calendar className="h-5 w-5" />
              </div>
              {t('dashboard.overview.tables.pendingClassRequests')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.pendingClassRequests.length > 0 ? <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600 dark:text-gray-400">{t('dashboard.overview.tables.student')}</TableHead>
                    <TableHead className="text-slate-600 dark:text-gray-400">{t('dashboard.overview.tables.requestedDate')}</TableHead>
                    <TableHead className="text-slate-600 dark:text-gray-400">{t('dashboard.overview.tables.waiting')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dashboardData.pendingClassRequests.map(request => <TableRow key={request.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/30">
                      <TableCell className="font-medium text-slate-700 dark:text-gray-300">
                        {request.profiles.first_name} {request.profiles.last_name}
                      </TableCell>
                      <TableCell className="text-slate-700 dark:text-gray-300">
                        {format(new Date(request.requested_date), "dd/MM/yyyy HH:mm", {
                    locale: dateLocale
                  })}
                      </TableCell>
                      <TableCell className="text-slate-500 dark:text-gray-400">
                        {formatDistanceToNow(new Date(request.created_at), {
                    locale: dateLocale,
                    addSuffix: true
                  })}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table> : <p className="text-sm text-muted-foreground p-4">{t('dashboard.overview.tables.noClassRequests')}</p>}
          </CardContent>
        </Card>

        <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden">
          <CardHeader className="pb-3 relative">
            <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
            <CardTitle className="text-lg font-medium flex items-center gap-2 text-slate-700 dark:text-gray-300">
              <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
                <Clock className="h-5 w-5" />
              </div>
              {t('dashboard.overview.tables.upcomingEvents')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.upcomingEvents.length > 0 ? <div className="space-y-4">
                {dashboardData.upcomingEvents.map(event => <div key={event.id} className="bg-slate-50/80 dark:bg-gray-700/30 p-4 rounded-lg border border-slate-100/50 dark:border-gray-600/30 hover:bg-slate-100/80 dark:hover:bg-gray-700/50 transition-colors">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-slate-800 dark:text-gray-200">{event.title || "Aula agendada"}</div>
                        <div className="text-sm flex items-center mt-1 space-x-2">
                          <span className="flex items-center text-slate-500 dark:text-gray-400">
                            <Clock className="h-3 w-3 mr-1 inline" />
                            {format(new Date(event.start_time), "EEEE, dd 'de' MMMM, HH:mm", {
                        locale: dateLocale
                      })}
                          </span>
                        </div>
                        {event.student && <div className="text-sm mt-2">
                            <span className="text-slate-500 dark:text-gray-400">{language === 'pt' ? 'Aluno' : 'Student'}: </span>
                            <span className="font-medium text-slate-700 dark:text-gray-300">{event.student.first_name} {event.student.last_name}</span>
                          </div>}
                      </div>
                      <div className="bg-flame/10 text-flame dark:bg-flame/20 dark:text-flame/90 font-medium text-xs px-2.5 py-1.5 rounded-full">
                        {event.event_type === 'assessment' ? (language === 'pt' ? 'Avaliação' : 'Assessment') : (language === 'pt' ? 'Treino' : 'Training')}
                      </div>
                    </div>
                  </div>)}
              </div> : <p className="text-sm text-muted-foreground p-4">{t('dashboard.overview.tables.noUpcomingEvents')}</p>}
          </CardContent>
        </Card>
      </div>

      <Card className="border border-slate-100/50 dark:border-gray-700/50 shadow-md bg-white/90 dark:bg-gray-800/80 backdrop-blur-xl rounded-xl overflow-hidden">
        <CardHeader className="pb-3 relative">
          <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-r from-flame/40 to-flame/80 rounded-t-xl" />
          <CardTitle className="text-lg font-medium flex items-center gap-2 text-slate-700 dark:text-gray-300">
            <div className="p-2 rounded-full bg-flame/10 text-flame dark:bg-flame/20">
              <UserPlus className="h-5 w-5" />
            </div>
            {t('dashboard.overview.tables.newStudentRequests')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dashboardData.newStudentRequestsDetails.length > 0 ? <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-slate-600 dark:text-gray-400">{t('dashboard.overview.tables.name')}</TableHead>
                  <TableHead className="text-slate-600 dark:text-gray-400">{t('dashboard.overview.tables.contact')}</TableHead>
                  <TableHead className="text-slate-600 dark:text-gray-400">{t('dashboard.overview.tables.waiting')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dashboardData.newStudentRequestsDetails.map(request => <TableRow key={request.id} className="hover:bg-slate-50/50 dark:hover:bg-gray-700/30">
                    <TableCell className="font-medium text-slate-700 dark:text-gray-300">
                      {request.first_name} {request.last_name}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="text-sm text-slate-700 dark:text-gray-300">{request.email}</div>
                        {request.phone && <div className="text-xs text-slate-500 dark:text-gray-400">{request.phone}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-slate-500 dark:text-gray-400">
                      {formatDistanceToNow(new Date(request.created_at), {
                  locale: dateLocale,
                  addSuffix: true
                })}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table> : <p className="text-sm text-muted-foreground p-4">{t('dashboard.overview.tables.noStudentRequests')}</p>}
        </CardContent>
      </Card>
    </div>;
};
